/**
 * Fait : décrit les documents des collections `conversations`, `branches` et
 *   `messages`. Une conversation appartient à un projet — la LOI du critère 7,
 *   requise par construction — et un message est soit celui d'un membre, soit
 *   celui d'un assistant, deux formes strictes qui ne se mélangent pas.
 *
 * Ne fait pas : il ne diffuse rien en direct — le flux de génération passe par
 *   le canal temps réel (`canal.ts`) — et ne décide pas qui lit quoi. Le prix
 *   d'une génération ne vit pas ici non plus : voir `economie.ts`, la
 *   réservation d'usage porte l'argent, le message ne porte que le texte.
 */

import { z } from 'zod';

import { identifiant, nomDonne, versionDeRessource } from '../schemas.ts';
import { FOURNISSEURS } from './economie.ts';

// La borne du texte vit dans le contrat PUBLIC (`schemas-conversation.ts`) :
// le formulaire du client la partage. Réexportée ici pour ses consommateurs.
export { TAILLE_MAXIMALE_TEXTE_MESSAGE } from '../schemas-conversation.ts';
import { TAILLE_MAXIMALE_TEXTE_MESSAGE } from '../schemas-conversation.ts';

/** Un fil de travail dans un projet. `projetId` requis : c'est la loi. */
export const conversation = z
  .object({
    projetId: identifiant,
    titre: nomDonne,
    creeParId: identifiant,

    /** Arbitrage des écritures concurrentes (D-011) : version obligatoire. */
    version: versionDeRessource,

    creeLe: z.date(),
    misAJourLe: z.date(),
  })
  .strict();

export type Conversation = z.infer<typeof conversation>;

/**
 * Une branche d'une conversation. Régénérer une réponse crée une branche
 * (plan, phase 5) : l'ancienne lecture reste intacte, la nouvelle vit à côté.
 */
export const branche = z
  .object({
    /**
     * Dénormalisé depuis la conversation : les droits se décident par projet,
     * et l'autorisation ne doit pas traverser deux collections pour répondre.
     */
    projetId: identifiant,
    conversationId: identifiant,

    /** Message d'où la branche part — absent pour la branche initiale. */
    messageDOrigineId: identifiant.optional(),

    creeParId: identifiant,
    creeLe: z.date(),
  })
  .strict();

export type Branche = z.infer<typeof branche>;

/** Champs communs aux deux origines d'un message. */
const socleDeMessage = {
  /** Dénormalisé pour la même raison que sur la branche : les droits. */
  projetId: identifiant,
  conversationId: identifiant,
  brancheId: identifiant,

  /** Position dans la branche, à partir de zéro — l'ordre ne se devine pas. */
  rang: z.number().int().min(0),

  texte: z.string().min(1).max(TAILLE_MAXIMALE_TEXTE_MESSAGE),

  /** Un message se modifie (plan, phase 5) : version obligatoire (D-011). */
  version: versionDeRessource,

  creeLe: z.date(),
  misAJourLe: z.date(),
};

/**
 * Un message est écrit par un membre, ou produit par un assistant. Les deux
 * formes sont strictes : un message de membre qui porterait un identifiant de
 * génération est refusé, il ne devient pas « un peu assistant ».
 */
export const message = z.discriminatedUnion('origine', [
  z.object({ origine: z.literal('membre'), auteurId: identifiant, ...socleDeMessage }).strict(),
  z
    .object({
      origine: z.literal('assistant'),

      /** Le membre qui a déclenché la génération — l'unique payeur (D-013). */
      declencheurId: identifiant,

      generationId: identifiant,
      fournisseur: z.enum(FOURNISSEURS),
      identifiantModele: z.string().min(1),

      /** Posés à la fin de la génération, quand le fournisseur les annonce. */
      jetonsEntree: z.number().int().min(0).optional(),
      jetonsSortie: z.number().int().min(0).optional(),

      ...socleDeMessage,
    })
    .strict(),
]);

export type Message = z.infer<typeof message>;
